import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
/**
 * useSavedView — remembers the search text and sort order a user last
 * used on a list, so tabs reopen the way they left them.
 *
 * All of a user's views arrive in one GET, cached module-wide, so the six
 * Project tabs don't each fire their own request. Writes are debounced and
 * fire-and-forget: a failed save just means the list opens at its default
 * next time, which is not worth interrupting the user for.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SAVE_DEBOUNCE_MS = 700;

let viewsPromise = null;
let viewsCache = null;
const listeners = new Set();

const loadViews = () => {
  if (viewsCache) return Promise.resolve(viewsCache);
  if (!viewsPromise) {
    viewsPromise = axios
      .get(`${API}/user-views`)
      .then((r) => {
        viewsCache = r.data || {};
        return viewsCache;
      })
      .catch(() => {
        viewsPromise = null;
        return {};
      });
  }
  return viewsPromise;
};

/** Drop the cache so the next mount re-reads from the server (e.g. on login). */
export const resetSavedViews = () => {
  viewsPromise = null;
  viewsCache = null;
};

/**
 * Wipe every saved view for the current user, server-side, and snap all
 * mounted lists back to their defaults without needing a reload.
 */
export const resetAllSavedViews = async () => {
  await axios.delete(`${API}/user-views`);
  viewsCache = {};
  viewsPromise = Promise.resolve(viewsCache);
  listeners.forEach((fn) => fn());
};

/**
 * Subscribe to "reset all my filters" for lists whose filters are
 * transient (not saved views) but which the user still expects the
 * button to clear — e.g. the Projects list search box.
 */
export function useOnSavedViewsReset(onReset) {
  const cb = useRef(onReset);
  cb.current = onReset;
  useEffect(() => {
    const fn = () => cb.current?.();
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);
}

export function useSavedView(viewKey, defaultSort = 'created_desc') {  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState(defaultSort);
  const [viewLoaded, setViewLoaded] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    let alive = true;
    setViewLoaded(false);
    loadViews().then((views) => {
      if (!alive) return;
      const saved = views[viewKey];
      if (saved) {
        setSearch(saved.search || '');
        setSortBy(saved.sort_by || defaultSort);
      } else {
        setSearch('');
        setSortBy(defaultSort);
      }
      setViewLoaded(true);
    });
    return () => { alive = false; };
    /* eslint-disable-next-line */
  }, [viewKey]);

  useEffect(() => {
    if (!viewLoaded) return undefined;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const payload = { search, sort_by: sortBy === defaultSort ? '' : sortBy };
      if (viewsCache) {
        if (!payload.search && !payload.sort_by) delete viewsCache[viewKey];
        else viewsCache[viewKey] = payload;
      }
      axios.put(`${API}/user-views/${encodeURIComponent(viewKey)}`, payload).catch(() => {});
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer.current);
    /* eslint-disable-next-line */
  }, [search, sortBy, viewLoaded, viewKey]);

  const resetView = useCallback(() => {
    setSearch('');
    setSortBy(defaultSort);
  }, [defaultSort]);

  // A global "reset all my filters" must clear lists that are already
  // mounted, not just the ones opened afterwards.
  useEffect(() => {
    const onReset = () => {
      setSearch('');
      setSortBy(defaultSort);
    };
    listeners.add(onReset);
    return () => { listeners.delete(onReset); };
  }, [defaultSort]);

  return { search, setSearch, sortBy, setSortBy, viewLoaded, resetView };
}

export default useSavedView;
